/**
 * What every analytics surface shares.
 *
 * There are four of them now — the dashboard at `/analytics`, the money screen
 * at `/analytics/revenue`, the Usage card on an account, the Usage and revenue
 * card on an application — and each one answers a DIFFERENT route over the
 * SAME window, with the same two omission rules laid over the top. The parts that must not diverge between them live
 * here: the grant names, the picker that produces a window, and the three
 * notices that say what a window is not.
 *
 * The rule this file exists to keep is that a window is described by the
 * ANSWER and never by the ask. Every screen below hands its response's own
 * `window` to `WindowNotices`, so a clamped span is announced by the card that
 * was clamped rather than assumed from the URL that asked.
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Button, Card, Field, Input, Select } from "@empite/ui-components";
import type { AnalyticsWindow } from "../admin-client";
import { ASSUMED_EXPONENT, currencyExponent, formatUnits } from "./money-units";
import {
  DEFAULT_DAYS,
  dayOptions,
  endsBeforeAsk,
  pickerValue,
  rangeSearch,
  recentSearch,
  requestedUntil,
} from "./analytics-window";
import type { AnalyticsSearch } from "./analytics-window";

/**
 * The grants the analytics surfaces are drawn from, repeated here only to
 * decide what to draw. The Admin API re-checks every one of them and writes
 * each refusal to the audit trail.
 *
 * READ gates a whole SCREEN or a whole CARD rather than a control on it: every
 * figure in one comes from the single call behind it, so an operator without
 * it has nothing left to render. MONEY gates FIELDS inside the answer, and the
 * response's own `money_visible` is what actually drives them — this constant
 * only names the grant in the placeholder that replaces a figure. IDENTITY
 * gates the name and the email on a row, never the link to the account.
 */
export const READ = "analytics:read";
export const MONEY = "wallets:read";
export const IDENTITY = "users:read";
/**
 * What the answer is not, said once each.
 *
 * All three are about the window rather than the figures, which is why they
 * sit above the tiles rather than inside them: an operator who misreads which
 * days they are looking at misreads every number on the page at once.
 *
 * Handed the three fields off a response rather than the response itself, so
 * one implementation serves the dashboard and every card that borrows its
 * window. `currency` and `currencies` are BOTH absent without `wallets:read`,
 * and the currency notice then does not fire — which is right: it is a fact
 * about money, and a reader who may not see the money has no figure for it to
 * be a caveat about.
 */
export function WindowNotices({
  window: w,
  currency,
  currencies,
  search,
}: {
  window: AnalyticsWindow;
  currency: string | undefined;
  currencies: string[] | undefined;
  search: AnalyticsSearch;
}) {
  // The currencies the totals are NOT in. Named rather than counted: an
  // operator reading a dollar figure needs to know which other unit is hiding
  // behind it, and the reporting code itself appearing in that list would
  // read as if it were one of the omissions.
  const others = (currencies ?? []).filter((code) => code !== currency);
  // The ask, not the answer. The server clamps `until` down to `through`, so
  // the two are equal in the response whenever this fires — the fact worth
  // telling the operator is that the day they had in mind is not in it.
  const shortOfAsk = endsBeforeAsk(w.through, requestedUntil(search, new Date()));

  return (
    <>
      {w.clamped ? (
        <Alert tone="info" title="The window was moved">
          Analytics answers at most 90 days, and it ends on the last complete UTC day. What is
          below covers {w.since} to {w.until} — not the span that was asked for.
        </Alert>
      ) : null}
      {shortOfAsk ? (
        <Alert tone="info" title="Today is missing on purpose">
          Figures run to {w.through}; today is not a finished day.
        </Alert>
      ) : null}
      {others.length > 0 ? (
        <Alert tone="warning" title="More than one currency earned in this window">
          The money below is {currency} alone. These days also carry{" "}
          {others.join(", ")}, and adding two currencies together would be a figure in neither of
          them — the per-currency split is on{" "}
          {/* The same window, carried across: a reader sent to the split to
              resolve a figure they are looking at now wants the days they are
              looking at now, not the default thirty. */}
          <Link to="/analytics/revenue" search={search}>
            the revenue screen
          </Link>
          .
        </Alert>
      ) : null}
    </>
  );
}

/**
 * The window picker, whose value is the URL.
 *
 * One control and not two: `since`, `until` and `days` together over-determine
 * a window, and the Admin API answers 400 rather than choosing which of the
 * three to ignore. A screen with a live day count beside a live date range
 * would produce that combination just by being filled in, so the mode is a
 * single select and each submit clears what the other mode owns.
 *
 * The navigation is the CALLER's, handed in as `apply`. Every route this
 * picker sits on has a different path and a different set of path parameters,
 * and TanStack's `navigate` is typed on both — so a `to` prop here would be a
 * string this component could not check. What it owns is the ONE rule worth
 * sharing: whatever it hands over is a well-formed window, never the
 * over-determined combination the server refuses.
 *
 * `idPrefix` exists because the labels are `htmlFor`-bound: two pickers on one
 * document with one set of ids would point every label at the first one.
 *
 * It renders the FORM and not the Card around it. On the dashboard the window
 * is the whole page's and gets a card of its own; on a card that borrows the
 * window for one panel of somebody else's page, a second card headed "Window"
 * above it would read as a control over everything below.
 */
export function WindowPicker({
  search,
  busy,
  apply,
  idPrefix = "analytics",
}: {
  search: AnalyticsSearch;
  busy: boolean;
  apply: (next: AnalyticsSearch) => void;
  idPrefix?: string;
}) {
  const value = pickerValue(search);
  const [mode, setMode] = useState(value.kind === "range" ? "range" : String(value.days));
  const [since, setSince] = useState(search.since ?? "");
  const [until, setUntil] = useState(search.until ?? "");

  // The URL is the source of truth, so back and forward have to pull the
  // controls along with them — the same rule the audit and packages filters
  // follow.
  useEffect(() => {
    const next = pickerValue(search);
    setMode(next.kind === "range" ? "range" : String(next.days));
    setSince(search.since ?? "");
    setUntil(search.until ?? "");
  }, [search.since, search.until, search.days]);

  return (
    <form
      className="esm-filters"
      onSubmit={(event) => {
        event.preventDefault();
        apply(
          mode === "range"
            ? rangeSearch(since, until)
            : recentSearch(Number(mode) || DEFAULT_DAYS),
        );
      }}
    >
      <Field label="Show" htmlFor={`${idPrefix}-mode`}>
        <Select
          id={`${idPrefix}-mode`}
          value={mode}
          onChange={(event) => setMode(event.target.value)}
        >
          {dayOptions(value).map((days) => (
            <option key={days} value={String(days)}>
              Last {days} days
            </option>
          ))}
          <option value="range">A date range</option>
        </Select>
      </Field>
      {mode === "range" ? (
        <>
          {/* A date box and not the datetime-local the audit filter uses:
              these bounds are inclusive UTC DATES on the wire, and a
              wall-clock instant converted to UTC would move the boundary
              by a day for most of the planet. The value a date input holds
              is already YYYY-MM-DD, which is exactly what the API takes. */}
          <Field
            label="From"
            htmlFor={`${idPrefix}-since`}
            hint="Inclusive. Leave empty for the last complete day, counted back."
          >
            <Input
              id={`${idPrefix}-since`}
              type="date"
              value={since}
              onChange={(event) => setSince(event.target.value)}
            />
          </Field>
          <Field label="To" htmlFor={`${idPrefix}-until`} hint="Inclusive.">
            <Input
              id={`${idPrefix}-until`}
              type="date"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </Field>
        </>
      ) : null}
      {/* Directly in the flex row, like the audit filter's: the row is
          aligned to its baseline, so the button lands level with the boxes
          without a wrapper of its own. */}
      <Button type="submit" busy={busy}>
        Apply
      </Button>
    </form>
  );
}

/**
 * A money figure in units, or nothing at all.
 *
 * `visible` is the response's `money_visible`, which follows `wallets:read`.
 * When it is false the four money keys are ABSENT rather than zero, and this
 * answers null so the tile draws a placeholder naming the grant instead of a
 * figure the reader could not tell from a real one.
 *
 * The exponent comes from the currency the response names, never assumed:
 * micros per major unit is 10^6 for JPY and 10^8 for USD, so guessing is a
 * hundredfold error on one of the eight currencies the platform transacts in.
 * A window that earned nothing names no currency at all, and the figure is
 * then labelled "units" — the convention money-units.ts sets for exactly this.
 */
export function money(micros: number | undefined, visible: boolean, currency: string | undefined): string | null {
  if (!visible || micros === undefined) {
    return null;
  }
  const exponent = currencyExponent(currency) ?? ASSUMED_EXPONENT;
  return `${formatUnits(micros, exponent)} ${currency || "units"}`;
}

/** Why a money tile is blank. Names the grant, so an operator asks for it
 *  rather than raising a bug against a page that looks broken. */
export const WITHHELD = `Needs ${MONEY}, which this account does not hold. Not zero — withheld.`;

